import ApiPurchasePlans from "api/ApiPurchasePlans"
import { PURCHASE_PLAN } from "reducers/purchasePlan/purchase-plan-actions"
import { useAppSelector } from "reducers/store"
import { mapPurchasePlan } from "pages/purchase-plans/mappers/mapPurchasePlanForm"

import { useCallback } from "react"
import { useMutation, useQueryClient } from "react-query"
import { FIRST_STEPS } from "reducers/accounts/actions-types"
import useHandleApiError from "hooks/useHandleApiErrors"

export type PurchasePlanSubmitTypes = "create" | "update" | "delete" | "updatePublished"

const usePurchasePlanRequest = (
  submitType: PurchasePlanSubmitTypes,
  data: App.PurchasePlan,
  onSuccess: () => void,
  defaultErrorMessage: string
) => {
  const queryClient = useQueryClient()

  const successAction = () => {
    queryClient.invalidateQueries(PURCHASE_PLAN)
    queryClient.invalidateQueries(FIRST_STEPS)
    onSuccess()
  }

  const {
    identifiers: { purchasePlanId }
  } = useAppSelector((state) => state.purchasePlan)

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const createPlan = useMutation({
    mutationFn: async () => {
      const mappedData = mapPurchasePlan(data)

      return new ApiPurchasePlans().create(mappedData)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const updatePlan = useMutation({
    mutationFn: async () => {
      const mappedData = mapPurchasePlan(data)

      return new ApiPurchasePlans().update(mappedData, purchasePlanId)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const publishOrUnpublishPlan = useMutation({
    mutationFn: async () => {
      return new ApiPurchasePlans().updateIsPublished(purchasePlanId)
    },
    onSuccess: successAction,
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, defaultErrorMessage)
    }
  })

  const deletePlan = useMutation({
    mutationFn: async () => {
      return new ApiPurchasePlans().delete(purchasePlanId)
    },
    onSuccess: successAction
  })

  const onSubmit = useCallback(() => {
    switch (submitType) {
      case "create":
        createPlan.mutate()
        break
      case "update":
        updatePlan.mutate()
        break
      case "delete":
        deletePlan.mutate()
        break
      case "updatePublished":
        publishOrUnpublishPlan.mutate()
        break
      default:
        break
    }
  }, [submitType])

  return {
    onSubmit
  }
}

export default usePurchasePlanRequest
